import { useTranslation } from 'react-i18next'
import { MessageCircle, Send } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PhoneInputWithCode } from './phone-input-with-code'
import type { ContactMethod } from '../types'

interface ContactMethodFieldProps {
	selectedMethods: ContactMethod[]
	onMethodsChange: (methods: ContactMethod[]) => void
	whatsappCountryCode: string
	whatsappNumber: string
	telegramUsername: string
	onWhatsappCountryCodeChange: (code: string) => void
	onWhatsappNumberChange: (number: string) => void
	onTelegramUsernameChange: (username: string) => void
	disabled?: boolean
	errors?: {
		contactMethods?: string
		whatsappNumber?: string
		telegramUsername?: string
	}
}

const selectMethod = (
	methods: ContactMethod[],
	method: ContactMethod,
): ContactMethod[] =>
	methods.includes(method) ? [] : [method]

export function ContactMethodField({
	selectedMethods,
	onMethodsChange,
	whatsappCountryCode,
	whatsappNumber,
	telegramUsername,
	onWhatsappCountryCodeChange,
	onWhatsappNumberChange,
	onTelegramUsernameChange,
	disabled = false,
	errors,
}: ContactMethodFieldProps) {
	const { t } = useTranslation('auth')

	const isWhatsapp = selectedMethods.includes('whatsapp')
	const isTelegram = selectedMethods.includes('telegram')

	return (
		<div className="space-y-3">
			<Label>{t('signUpNew.step1.contactMethod')}</Label>

			<div className="grid grid-cols-2 gap-3">
				<button
					type="button"
					disabled={disabled}
					onClick={() => onMethodsChange(selectMethod(selectedMethods, 'whatsapp'))}
					className={cn(
						'flex items-center gap-2 rounded-lg border-2 p-3 text-left text-sm transition-colors',
						isWhatsapp
							? 'border-primary bg-primary/5 text-foreground'
							: 'border-border text-muted-foreground hover:border-primary/50',
						disabled && 'pointer-events-none opacity-50',
					)}
				>
					<MessageCircle className="h-4 w-4 shrink-0" />
					WhatsApp
				</button>

				<button
					type="button"
					disabled={disabled}
					onClick={() => onMethodsChange(selectMethod(selectedMethods, 'telegram'))}
					className={cn(
						'flex items-center gap-2 rounded-lg border-2 p-3 text-left text-sm transition-colors',
						isTelegram
							? 'border-primary bg-primary/5 text-foreground'
							: 'border-border text-muted-foreground hover:border-primary/50',
						disabled && 'pointer-events-none opacity-50',
					)}
				>
					<Send className="h-4 w-4 shrink-0" />
					Telegram
				</button>
			</div>

			{errors?.contactMethods && (
				<p className="text-destructive text-sm">{errors.contactMethods}</p>
			)}

			{isWhatsapp && (
				<div className="space-y-1.5">
					<Label className="text-xs">{t('signUpNew.step1.whatsappNumber')}</Label>
					<PhoneInputWithCode
						countryCode={whatsappCountryCode}
						phoneNumber={whatsappNumber}
						onCountryCodeChange={onWhatsappCountryCodeChange}
						onPhoneNumberChange={onWhatsappNumberChange}
						disabled={disabled}
						error={!!errors?.whatsappNumber}
					/>
					{errors?.whatsappNumber && (
						<p className="text-destructive text-sm">{errors.whatsappNumber}</p>
					)}
				</div>
			)}

			{isTelegram && (
				<div className="space-y-1.5">
					<Label className="text-xs">{t('signUpNew.step1.telegramUsername')}</Label>
					<div className="relative">
						<span className="text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 text-sm">
							@
						</span>
						<Input
							placeholder="username"
							value={telegramUsername}
							onChange={(e) => onTelegramUsernameChange(e.target.value)}
							disabled={disabled}
							error={!!errors?.telegramUsername}
							className="pl-7"
						/>
					</div>
					{errors?.telegramUsername && (
						<p className="text-destructive text-sm">{errors.telegramUsername}</p>
					)}
				</div>
			)}
		</div>
	)
}
